import { useQuery } from 'react-query'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageTopBar, PageView } from '../../components'
import { fetchWarehouse } from '../../services/warehouseSevices'
import { fetchProofVideoCoverage, refusalKind } from '../../services/proofVideoCoverageService'
import { retryTransient, withinRetryBudget } from '../../helpers/retryTransient'
import { FIVE_MINUTES_MS } from '../../helpers/cacheTimes'
import { dateInTimezone } from '../../utils/warehouseDates'
import { resolvePeriod } from './coveragePeriod'
import { formatShortDate, headlineParts } from './coverageFormat'
import { CoveragePeriodPicker } from './CoveragePeriodPicker'
import { CoverageTable } from './CoverageTable'
import { retryButton } from './coverageStyles'
import { COVERAGE_I18N, PERIOD_I18N } from './i18nKeys'

const alertBox = 'rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800'
const VISIBLE_STATUS = ['loading', 'empty']
// Statuses whose alert is just the message of the same name.
const PLAIN_ALERTS = ['time_zone_missing', 'forbidden', 'not_found']

const retryCoverage = (failureCount, error) => error.transient && withinRetryBudget(failureCount)

const warehouseStatus = warehouseQuery => {
  if (warehouseQuery.isError) return refusalKind(warehouseQuery.error?.response?.status) ?? 'failed'
  if (warehouseQuery.isSuccess) return 'time_zone_missing'
  return 'loading'
}

const coverageStatus = coverageQuery => {
  if (coverageQuery.isError) return coverageQuery.error.kind
  if (!coverageQuery.isSuccess) return 'loading'
  return coverageQuery.data.packed === 0 ? 'empty' : 'ready'
}

const screenStatus = ({ today, warehouseQuery, periodError, coverageQuery }) => {
  if (!today) return warehouseStatus(warehouseQuery)
  if (periodError) return 'invalid_period'
  return coverageStatus(coverageQuery)
}

export const ProofVideoCoverageContainer = () => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { warehouse_id } = useParams()
  const { pathname, search } = useLocation()
  const history = useHistory()

  const warehouseQuery = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    refetchOnWindowFocus: false,
    retry: retryTransient,
  })
  const timeZone = warehouseQuery.data?.time_zone || null
  const today = dateInTimezone(new Date().toISOString(), timeZone)

  const { period, error: periodError } = today ? resolvePeriod(search, today) : { period: null, error: null }

  const coverageQuery = useQuery(['proof_video_coverage', warehouse_id, period?.from, period?.to], () => fetchProofVideoCoverage(warehouse_id, period), {
    enabled: !!today && !periodError,
    refetchOnWindowFocus: false,
    retry: retryCoverage,
  })

  const changePeriod = next => history.push({ pathname, search: `?from=${next.from}&to=${next.to}` })
  const retryFailed = () => (today ? coverageQuery.refetch() : warehouseQuery.refetch())

  const status = screenStatus({ today, warehouseQuery, periodError, coverageQuery })
  const sent = period ? { from: formatShortDate(period.from, language), to: formatShortDate(period.to, language) } : {}
  const coverage = coverageQuery.data

  const statusText = () => {
    if (status === 'loading') return t(`${COVERAGE_I18N}.states.loading`)
    if (status === 'empty') return t(`${COVERAGE_I18N}.states.empty`, sent)
    if (status === 'ready') {
      const { rate, ofPacked } = headlineParts(coverage, t, language)
      return `${rate} ${ofPacked}`
    }
    return ''
  }

  const withReason = (sentence, reason) => {
    const key = `${PERIOD_I18N}.errors.${reason}`
    return reason && i18n.exists(key) ? `${sentence} ${t(key)}` : sentence
  }

  const alert = text => (
    <p role="alert" className={alertBox}>
      {text}
    </p>
  )

  const renderBody = () => {
    if (PLAIN_ALERTS.includes(status)) return alert(t(`${COVERAGE_I18N}.states.${status}`))
    switch (status) {
      case 'invalid_period':
        return alert(withReason(t(`${COVERAGE_I18N}.states.invalid_period`), periodError))
      case 'rejected':
        return alert(withReason(t(`${COVERAGE_I18N}.states.rejected`, sent), coverageQuery.error.reason))
      case 'failed':
        return (
          <div role="alert" className={`${alertBox} flex items-center gap-3`}>
            <span>{t(`${COVERAGE_I18N}.states.failed`)}</span>
            <button type="button" className={retryButton} onClick={retryFailed}>
              {t(`${COVERAGE_I18N}.states.retry`)}
            </button>
          </div>
        )
      case 'ready':
        return <CoverageTable coverage={coverage} />
      default:
        return null
    }
  }

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <h1 className="text-lg font-semibold">{t(`${COVERAGE_I18N}.title`)}</h1>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <div className="flex flex-col gap-5">
        {today && <CoveragePeriodPicker period={period} today={today} onChange={changePeriod} />}
        <section aria-busy={status === 'loading'}>
          <p role="status" className={VISIBLE_STATUS.includes(status) ? 'text-sm text-gray-700' : 'sr-only'}>
            {statusText()}
          </p>
          {renderBody()}
        </section>
      </div>
    </PageView>
  )
}
